/**
 * Billing Success Page — /billing/success?session_id=cs_...
 *
 * Shown after the Stripe Checkout return URL fires. Three behaviours:
 *   1. Payment is confirmed but Aviatto is still provisioning the tenant
 *      (polls /billing/checkout-invite/ until the backend returns a stable
 *      state).
 *   2. An email verification code must be confirmed before an account
 *      invite is issued (post-payment fraud / ownership check).
 *   3. Everything is ready — we either show an "Ativar conta" CTA (if the
 *      backend emitted an invite token) or a "Acessar plataforma" login
 *      CTA when the account already exists.
 *
 * Industry design system (auth grammar): Z plate + wordmark over a centered
 * blueprint card on the light ground. Status notes are grammar boxes (accent
 * border + 7% tint for positive/info, ink border for blocking), order facts
 * are dashed hairline rows with heading-font values, activation steps are
 * square glyph-plate rows. No navy header, no ticket stripe, no glow dot,
 * no pills, no monospace. All polling/activation logic is untouched.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle as CheckCircleIcon,
  PersonAdd as PersonAddIcon,
  Login as LoginIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import apiClient from '../services/api';
import authService from '../services/auth';
import axios from 'axios';
import SubscriptionStepper from '../components/billing/SubscriptionStepper';

const LOCAL_BACKEND = 'http://127.0.0.1:8000/api';
const PUBLIC_CHECKOUT_HANDOFF_KEY = 'aviatto:publicCheckoutHandoff';

// Polling tuning for the provisioning state-machine.
//
// `STALL_AFTER_ATTEMPTS` is the threshold past which we surface a clear
// recovery UI to the user — they have waited long enough that they
// deserve an explicit message and an escape hatch instead of a silent
// spinner. We KEEP polling in the background after the stall threshold
// so that if Celery eventually finishes the provisioning task, the
// activation CTA appears automatically.
//
// `MAX_POLL_ATTEMPTS` is the absolute ceiling. Keep it comfortably above
// the real production tenant migration window: large schemas have taken
// just over 4 minutes, and stopping at that boundary stranded users seconds
// before the activation token appeared.
export const STALL_AFTER_ATTEMPTS = 12; // ~30s with the 2-3s spacing below
export const MAX_POLL_ATTEMPTS = 360;   // ~18 min of total background polling
export const SUPPORT_EMAIL = 'support@zulbot.ai';

/**
 * Pure helper deciding the next state of the post-payment provisioning
 * state-machine. Extracted so we can unit-test the rules without
 * mounting the page or driving the real polling loop.
 *
 * @param {number} attempts - how many `provisioning:true` polls in a
 *   row we have already observed (must be incremented BEFORE calling).
 * @param {{stallAfter?: number, maxAttempts?: number}} [config]
 *   optional override knobs (defaults to the module-level constants).
 * @returns {{
 *   stalled: boolean,
 *   exhausted: boolean,
 *   shouldScheduleNextPoll: boolean,
 *   nextPollDelayMs: number,
 * }}
 */
export function decideProvisioningPollStep(attempts, config = {}) {
  const stallAfter = config.stallAfter ?? STALL_AFTER_ATTEMPTS;
  const maxAttempts = config.maxAttempts ?? MAX_POLL_ATTEMPTS;
  const stalled = attempts >= stallAfter;
  const exhausted = attempts > maxAttempts;
  // Polls 1-9 are spaced 2s for a snappier first-impression; once we
  // pass that, drop to 3s to avoid hammering the API while we wait
  // for Celery.
  const nextPollDelayMs = attempts < 10 ? 2000 : 3000;
  return {
    stalled,
    exhausted,
    shouldScheduleNextPoll: !exhausted,
    nextPollDelayMs,
  };
}

export function buildActivationPath({ inviteToken, email = '', tenantSchema = '' }) {
  const params = new URLSearchParams({ token: inviteToken });
  if (email) params.set('email', email);
  if (tenantSchema) params.set('tenant_schema', tenantSchema);
  return `/activate-account?${params.toString()}`;
}

export function buildOnboardingActivationPath({ onboardingToken = '', activationPath = '' }) {
  const path = String(activationPath || '').trim();
  if (path.startsWith('/onboarding/activate')) {
    return path;
  }
  const params = new URLSearchParams({ token: onboardingToken });
  return `/onboarding/activate?${params.toString()}`;
}

function isJsdomRuntime() {
  try {
    return typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '');
  } catch {
    return false;
  }
}

export function redirectToOnboardingActivation(activationPath, navigate) {
  navigate(activationPath, { replace: true });

  if (
    typeof window === 'undefined' ||
    !window.location ||
    typeof window.setTimeout !== 'function' ||
    typeof window.location.assign !== 'function' ||
    isJsdomRuntime()
  ) {
    return;
  }

  window.setTimeout(() => {
    try {
      if (window.location.pathname === '/onboarding/activate') {
        return;
      }
      window.location.assign(activationPath);
    } catch {
      // React Router navigation already ran above; this is only a hard
      // redirect fallback for browsers that somehow stay on success.
    }
  }, 150);
}

function isRunningOnLocalhost() {
  try {
    const h = window.location.hostname;
    return h === 'localhost' || h === '127.0.0.1';
  } catch {
    return false;
  }
}

async function localFallbackPost(url, body, opts) {
  try {
    const resp = await apiClient.post(url, body, opts);
    return resp;
  } catch (err) {
    if (!isRunningOnLocalhost()) throw err;
    const { data } = await axios.post(`${LOCAL_BACKEND}${url}`, body, {
      headers: { 'Content-Type': 'application/json' },
      timeout: opts?.timeout || 20000,
    });
    return { data };
  }
}

async function localFallbackGet(url, opts) {
  try {
    const resp = await apiClient.get(url, opts);
    return resp;
  } catch (err) {
    if (!isRunningOnLocalhost()) throw err;
    const queryStr = opts?.params
      ? '?' + new URLSearchParams(opts.params).toString()
      : '';
    const { data } = await axios.get(`${LOCAL_BACKEND}${url}${queryStr}`, {
      headers: { 'Content-Type': 'application/json' },
      timeout: opts?.timeout || 20000,
    });
    return { data };
  }
}

const NEXT_STEPS = [
  {
    code: '01',
    title: 'Cadastrar primeira aeronave',
    description: 'Prefixo e tipo — leva menos de 2 minutos.',
  },
  {
    code: '02',
    title: 'Convidar tripulação & gestores',
    description: 'Até 12 usuários inclusos no plano Pro.',
  },
  {
    code: '03',
    title: 'Conectar agenda & manutenção',
    description: 'Importe o histórico via CSV ou integração.',
  },
];

function formatNextBillingDate() {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Industry grammar tokens / shared style fragments ──────────────────────
const HEADING = 'var(--font-heading)';
const INK = 'var(--color-neutral-900)';
const ACC = 'var(--color-accent)';
const ACC7 = 'var(--color-accent-700)';
const ACC8 = 'var(--color-accent-800)';
const MUTED55 = 'color-mix(in srgb, var(--color-text) 55%, transparent)';
const MUTED58 = 'color-mix(in srgb, var(--color-text) 58%, transparent)';
const MUTED72 = 'color-mix(in srgb, var(--color-text) 72%, transparent)';

const labelSx = {
  display: 'block',
  fontFamily: HEADING,
  fontSize: '9.5px',
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: MUTED58,
  mb: '4px',
};

const inputStyle = {
  width: '100%',
  minHeight: 38,
  fontSize: 14,
  background: 'transparent',
  border: '1px solid var(--color-divider)',
  borderRadius: 0,
  padding: '0 11px',
  boxSizing: 'border-box',
  fontFamily: 'var(--font-body)',
  color: 'var(--color-text)',
};

const primaryBtnSx = {
  width: '100%',
  minHeight: 44,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  border: `1px solid ${ACC}`,
  background: ACC,
  color: 'var(--color-bg)',
  fontFamily: HEADING,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  borderRadius: 0,
  cursor: 'pointer',
  '&:hover': { background: 'var(--color-accent-600)', borderColor: 'var(--color-accent-600)' },
  '&:active': { background: ACC7 },
  '&:disabled': { opacity: 0.45, cursor: 'default' },
};

const secondaryBtnSx = {
  width: '100%',
  minHeight: 38,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  px: '14px',
  border: '1px solid var(--color-divider)',
  background: 'transparent',
  color: 'var(--color-text)',
  fontFamily: HEADING,
  fontSize: '11.5px',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  borderRadius: 0,
  cursor: 'pointer',
  '&:hover': { borderColor: 'rgba(29,31,32,0.45)' },
  '&:disabled': { opacity: 0.45, cursor: 'default' },
};

const linkBtnSx = {
  border: 0,
  background: 'transparent',
  p: 0,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '7px',
  fontFamily: HEADING,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: ACC7,
  '&:hover': { color: ACC8 },
  '&:disabled': { opacity: 0.45, cursor: 'default' },
};

/** Accent-bordered 7%-tint note (positive/informational states). */
function AccentNote({ title, children, sx }) {
  return (
    <Box
      sx={{
        p: '12px 13px',
        border: `1px solid ${ACC}`,
        background: 'color-mix(in srgb, var(--color-accent) 7%, transparent)',
        color: ACC8,
        fontSize: 13,
        lineHeight: 1.55,
        ...sx,
      }}
    >
      {title ? <Box sx={{ fontWeight: 700, mb: '3px' }}>{title}</Box> : null}
      {children}
    </Box>
  );
}

/** Ink-bordered grammar box (errors / blocking notes). */
function InkNote({ children, sx }) {
  return (
    <Box sx={{ p: '9px 11px', border: `1px solid ${INK}`, fontSize: 12.5, lineHeight: 1.45, color: INK, ...sx }}>
      {children}
    </Box>
  );
}

function ZPlateBrand() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', color: 'var(--color-text)' }}>
      <Box component="span" sx={{ display: 'grid', placeItems: 'center', width: 30, height: 30, border: '1.5px solid var(--color-text)', fontFamily: HEADING, fontSize: 15, fontWeight: 600 }}>
        Z
      </Box>
      <Box component="span" sx={{ fontFamily: HEADING, fontSize: 19, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        Zulbot
      </Box>
    </Box>
  );
}

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [needsAccount, setNeedsAccount] = useState(false);
  const [inviteToken, setInviteToken] = useState('');
  // Write-only state kept for parity with the polling contract: the backend
  // reports invite_url/subdomain, but the page navigates via inviteToken.
  const [, setInviteUrl] = useState('');
  const [email, setEmail] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [, setSubdomain] = useState('');
  const [tenantSchema, setTenantSchema] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [provisioning, setProvisioning] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');

  // Recovery state for the "provisioning is taking longer than expected"
  // case. See the `STALL_AFTER_ATTEMPTS` / `MAX_POLL_ATTEMPTS` block at
  // the top of the file for the full rationale.
  const [provisioningStalled, setProvisioningStalled] = useState(false);
  const [pollExhausted, setPollExhausted] = useState(false);
  const [resendingActivation, setResendingActivation] = useState(false);
  const [activationResendInfo, setActivationResendInfo] = useState('');
  const [activationResendError, setActivationResendError] = useState('');
  const [supportFormOpen, setSupportFormOpen] = useState(false);
  const [supportForm, setSupportForm] = useState({
    name: '',
    email: '',
    company_name: '',
    description: '',
  });
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportError, setSupportError] = useState('');
  const [supportSuccess, setSupportSuccess] = useState('');

  const pollRef = useRef({ timeoutId: null, attempts: 0 });

  useEffect(() => {
    // Any landing on /billing/success?session_id=... is a Stripe checkout
    // return. Whatever session was in this browser belongs to whoever was
    // signed in before the checkout (often the Aviato operator who generated
    // an admin trial-checkout link), NOT the new customer we're about to
    // provision. Logging out unconditionally here closes the operator-session
    // leak vector PR #15 left open for the invite-token (admin-generated)
    // checkout flow: without this, the still-valid operator JWT would let
    // the browser navigate back into /dashboard and render the operator's
    // own tenant data while we're still polling for the new tenant's invite.
    if (sessionId) {
      try {
        authService.logout();
      } catch {
        // best-effort; local-only logout shouldn't throw, but never let
        // this stop the polling flow.
      }
      try {
        sessionStorage.removeItem(PUBLIC_CHECKOUT_HANDOFF_KEY);
      } catch {
        // best-effort
      }
    }
    checkInviteStatus();
    return () => {
      if (pollRef.current.timeoutId) {
        clearTimeout(pollRef.current.timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const checkInviteStatus = async () => {
    if (!sessionId) {
      setError('Sessão de pagamento não encontrada. Volte e tente novamente.');
      setLoading(false);
      return;
    }

    try {
      const response = await localFallbackGet('/billing/checkout-invite/', {
        params: { session_id: sessionId },
        __skipAuthRefresh: true,
        __skipAuthHeader: true,
        timeout: 20000,
      });

      const data = response.data;

      if (data.needs_account && data.verification_required) {
        setNeedsAccount(true);
        setInviteToken('');
        setInviteUrl('');
        setEmail(data.email || '');
        setMaskedEmail(data.masked_email || data.email || '');
        setSubdomain(data.subdomain || '');
        if (data.tenant_schema) setTenantSchema(data.tenant_schema);
        setCompanyName(data.company_name || '');
        setVerificationRequired(true);
        setVerificationToken(data.verification_token || '');
        setVerificationMessage(data.verification_code_sent ? 'Enviamos um codigo para o email da compra.' : '');
        setProvisioning(false);
      } else if (data.needs_account && data.onboarding_v2 && (data.activation_path || data.onboarding_token)) {
        const activationPath = buildOnboardingActivationPath({
          onboardingToken: data.onboarding_token || '',
          activationPath: data.activation_path || '',
        });
        setNeedsAccount(true);
        setInviteToken('');
        setInviteUrl(data.activation_url || activationPath);
        setEmail(data.email || '');
        setMaskedEmail(data.masked_email || data.email || '');
        setSubdomain(data.subdomain || '');
        setCompanyName(data.company_name || '');
        setVerificationRequired(false);
        setVerificationToken('');
        setVerificationMessage('');
        setProvisioning(false);
        setProvisioningStalled(false);
        setPollExhausted(false);
        redirectToOnboardingActivation(activationPath, navigate);
      } else if (data.needs_account && data.invite_token) {
        const resolvedTenantSchema = data.tenant_schema || tenantSchema || '';
        setNeedsAccount(true);
        setInviteToken(data.invite_token);
        setInviteUrl(data.invite_url || '');
        setEmail(data.email || '');
        setMaskedEmail(data.masked_email || data.email || '');
        setSubdomain(data.subdomain || '');
        if (resolvedTenantSchema) setTenantSchema(resolvedTenantSchema);
        setCompanyName(data.company_name || '');
        setVerificationRequired(false);
        setVerificationToken('');
        setVerificationMessage('');
        setProvisioning(false);
        // The activation token has finally arrived — clear any stalled
        // recovery panel so the user / AI agent gets the regular
        // "Criar Minha Conta" CTA instead of the recovery banner.
        setProvisioningStalled(false);
        setPollExhausted(false);
        navigate(
          buildActivationPath({
            inviteToken: data.invite_token,
            email: data.email || email,
            tenantSchema: resolvedTenantSchema,
          }),
          { replace: true },
        );
      } else {
        setNeedsAccount(false);
        setVerificationRequired(false);
        setVerificationToken('');
        setVerificationMessage('');

        if (data?.email) setEmail(data.email);
        if (data?.masked_email) setMaskedEmail(data.masked_email);
        if (data?.company_name) setCompanyName(data.company_name);
        if (data?.tenant_schema) setTenantSchema(data.tenant_schema);

        const isProvisioning = Boolean(data?.provisioning);
        setProvisioning(isProvisioning);

        if (isProvisioning) {
          pollRef.current.attempts += 1;
          const step = decideProvisioningPollStep(pollRef.current.attempts);

          if (step.stalled) {
            setProvisioningStalled(true);
          }

          if (step.shouldScheduleNextPoll) {
            pollRef.current.timeoutId = setTimeout(() => {
              checkInviteStatus();
            }, step.nextPollDelayMs);
          } else {
            // Stop polling but DO NOT send the user to a tokenless
            // /register page (that was the silent-failure trap). Stay
            // on the recovery panel; the resend / support / re-check
            // controls below give them an explicit way out.
            setPollExhausted(true);
            setProvisioningStalled(true);
          }
        }
      }
    } catch (e) {
      console.error('Error checking invite status:', e);

      pollRef.current.attempts += 1;
      const step = decideProvisioningPollStep(pollRef.current.attempts);
      if (step.stalled) {
        setProvisioningStalled(true);
      }
      if (step.shouldScheduleNextPoll) {
        setProvisioning(true);
        pollRef.current.timeoutId = setTimeout(() => {
          checkInviteStatus();
        }, step.nextPollDelayMs);
      } else {
        setProvisioning(false);
        setPollExhausted(true);
        setProvisioningStalled(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // User-driven controls for the provisioning recovery panel. These are
  // the explicit escape hatches that replace the historical silent
  // navigation to `/register?email=...` on stall.
  const handleRecheckNow = () => {
    setActivationResendInfo('');
    setActivationResendError('');
    if (pollRef.current.timeoutId) {
      clearTimeout(pollRef.current.timeoutId);
      pollRef.current.timeoutId = null;
    }
    // If the FE had already given up (`pollExhausted`), allow the user
    // to start a fresh budget on demand. Capped via MAX_POLL_ATTEMPTS
    // so we can never poll forever.
    if (pollExhausted) {
      pollRef.current.attempts = 0;
      setPollExhausted(false);
    }
    setProvisioning(true);
    checkInviteStatus();
  };

  const handleResendActivationEmail = async () => {
    if (!email) {
      setActivationResendError(
        'Não conseguimos identificar o e-mail da compra para reenviar o link.',
      );
      return;
    }
    setResendingActivation(true);
    setActivationResendInfo('');
    setActivationResendError('');
    try {
      // Endpoint is anti-enumerative (always returns 200 {ok:true}); we
      // therefore only show a generic confirmation regardless of the
      // server-side outcome. Errors caught below are network-level only.
      await localFallbackPost(
        '/auth/resend-invite/',
        {
          email,
          ...(tenantSchema ? { tenant_schema: tenantSchema } : {}),
        },
        {
          __skipAuthRefresh: true,
          __skipAuthHeader: true,
          timeout: 15000,
        },
      );
      setActivationResendInfo(
        `Reenviamos o link de ativação para ${maskedEmail || email}. Confira sua caixa de entrada e a pasta de spam.`,
      );
    } catch (err) {
      setActivationResendError(
        'Não foi possível reenviar agora. Tente novamente em alguns instantes ou contate o suporte.',
      );
    } finally {
      setResendingActivation(false);
    }
  };

  const openSupportForm = () => {
    setSupportFormOpen(true);
    setSupportError('');
    setSupportSuccess('');
    setSupportForm((current) => ({
      ...current,
      email: current.email || email || '',
      company_name: current.company_name || companyName || '',
      description:
        current.description ||
        `Pagamento confirmado, mas ainda não consegui criar minhas credenciais. Sessão Stripe: ${sessionId || '-'}.`,
    }));
  };

  const handleSupportFieldChange = (field, value) => {
    setSupportForm((current) => ({ ...current, [field]: value }));
    setSupportError('');
    setSupportSuccess('');
  };

  const handleSubmitSupportRequest = async (event) => {
    event.preventDefault();
    const payload = {
      name: supportForm.name.trim(),
      email: supportForm.email.trim(),
      company_name: supportForm.company_name.trim(),
      issue_type: 'account_access',
      description: supportForm.description.trim(),
      source_page: 'billing_success',
    };

    if (!payload.name || !payload.email || payload.description.length < 10) {
      setSupportError('Preencha nome, e-mail e uma descrição curta do problema.');
      return;
    }

    setSupportSubmitting(true);
    setSupportError('');
    setSupportSuccess('');
    try {
      const response = await authService.createAccessSupportRequest(payload);
      setSupportSuccess(
        response?.ticket_id
          ? `Pedido enviado. Ticket #${response.ticket_id}.`
          : 'Pedido enviado. Nossa equipe vai acompanhar a ativação.',
      );
    } catch (err) {
      setSupportError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          `Não foi possível abrir o chamado agora. Envie um e-mail para ${SUPPORT_EMAIL}.`,
      );
    } finally {
      setSupportSubmitting(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationToken) {
      setError('Codigo de verificacao indisponivel. Atualize a pagina para tentar novamente.');
      return;
    }
    if (!verificationCode.trim()) {
      setError('Informe o codigo de verificacao enviado por email.');
      return;
    }

    setVerifyingCode(true);
    setError('');
    setVerificationMessage('');
    try {
      const response = await localFallbackPost(
        '/billing/checkout-verification/verify/',
        {
          verification_token: verificationToken,
          code: verificationCode.trim(),
        },
        {
          __skipAuthRefresh: true,
          __skipAuthHeader: true,
          timeout: 20000,
        }
      );

      const data = response.data || {};
      setVerificationRequired(Boolean(data.verification_required));
      setInviteToken(data.invite_token || '');
      setInviteUrl(data.invite_url || '');
      setVerificationToken('');
      setVerificationCode('');
      setVerificationMessage('Email validado com sucesso. Agora você pode ativar seu acesso.');
      if (data.email) setEmail(data.email);
      if (data.masked_email) setMaskedEmail(data.masked_email);
      if (data.company_name) setCompanyName(data.company_name);
    } catch (e) {
      setError(e?.response?.data?.error || 'Nao foi possivel validar o codigo informado.');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleResendCode = async () => {
    if (!verificationToken) {
      setError('Codigo de verificacao indisponivel. Atualize a pagina para tentar novamente.');
      return;
    }

    setResendingCode(true);
    setError('');
    setVerificationMessage('');
    try {
      const response = await localFallbackPost(
        '/billing/checkout-verification/resend/',
        { verification_token: verificationToken },
        {
          __skipAuthRefresh: true,
          __skipAuthHeader: true,
          timeout: 20000,
        }
      );

      const data = response.data || {};
      if (data.masked_email) {
        setMaskedEmail(data.masked_email);
      }
      setVerificationMessage(data.message || 'Enviamos um novo codigo para o email cadastrado.');
    } catch (e) {
      setError(e?.response?.data?.error || 'Nao foi possivel reenviar o codigo agora.');
    } finally {
      setResendingCode(false);
    }
  };

  const handleCreateAccount = () => {
    authService.logout();

    if (inviteToken) {
      navigate(buildActivationPath({ inviteToken, email, tenantSchema }));
      return;
    }

    // Defensive: if the CTA is somehow reached without an invite_token
    // (e.g. a future regression in the polling state-machine), flip
    // into the recovery panel instead of navigating to a tokenless
    // /register page that would silently land the user on /login.
    setProvisioningStalled(true);
    setActivationResendError(
      'Ainda não recebemos o token de ativação. Use os botões abaixo para reenviar o link ou contatar o suporte.',
    );
  };

  const handleGoToLogin = () => {
    authService.logout();
    const params = new URLSearchParams();
    if (email) params.set('email', email);
    // Always carry tenant_schema through to /login so the user is
    // routed to *their* tenant (not the hostname-default one). Missing
    // this hint was the original cross-tenant data-leak vector.
    if (tenantSchema) params.set('tenant_schema', tenantSchema);
    const query = params.toString();
    navigate(query ? `/login?${query}` : '/login');
  };

  const activeStepIndex = provisioning ? 2 : needsAccount && verificationRequired ? 1 : 2;
  const allStepsDone = !provisioning && !needsAccount;

  const receiptLines = useMemo(
    () => [
      ['Plano', 'Zulbot Pro · Mensal'],
      ['Valor pago', 'R$ 500,00 BRL'],
      ['Próxima cobrança', formatNextBillingDate()],
      ['Operação', companyName || 'Sua empresa'],
      ['Email', maskedEmail || email || '—'],
    ],
    [companyName, email, maskedEmail]
  );

  if (loading) {
    return (
      <Box
        className="zb-industry"
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          fontFamily: 'var(--font-body)',
        }}
      >
        <ZPlateBrand />
        <CircularProgress size={28} sx={{ color: ACC }} />
        <Typography sx={{ fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: MUTED58 }}>
          Verificando seu pagamento...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="zb-industry"
      sx={{
        minHeight: '100dvh',
        background: 'var(--color-bg)',
        color: 'var(--color-text)',
        fontFamily: 'var(--font-body)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: '32px 24px 44px',
        boxSizing: 'border-box',
      }}
    >
      <ZPlateBrand />

      <Box
        className="blueprint"
        sx={{ width: '100%', maxWidth: 620, mt: 3, p: 0, background: 'transparent', boxShadow: 'var(--ind-shadow-md)' }}
      >
        <i className="corner tl" /><i className="corner tr" /><i className="corner bl" /><i className="corner br" />

        {/* ── Header band: confirmation chips + uppercase title ── */}
        <Box sx={{ p: '22px 26px 18px', borderBottom: '1px dashed var(--color-neutral-300)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                p: '3px 9px',
                border: `1px solid ${ACC}`,
                background: 'color-mix(in srgb, var(--color-accent) 8%, transparent)',
                fontFamily: HEADING,
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: ACC8,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 13 }} />
              Pagamento confirmado
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
              <Box component="span" sx={{ width: 5, height: 5, background: ACC }} />
              <Box component="span" sx={{ fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: ACC7 }}>
                Assinatura ativa
              </Box>
            </Box>
          </Box>

          <Typography
            component="h1"
            sx={{ m: '12px 0 0', fontFamily: HEADING, fontSize: 28, fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.01em', textTransform: 'uppercase' }}
          >
            Pagamento Concluído!
          </Typography>
          <Typography sx={{ m: '5px 0 0', fontSize: 13.5, lineHeight: 1.5, color: MUTED72 }}>
            Sua assinatura Zulbot foi confirmada com sucesso.
          </Typography>

          <SubscriptionStepper activeIndex={activeStepIndex} allDone={allStepsDone} sx={{ mt: '16px' }} />
        </Box>

        <Box sx={{ p: '20px 26px 24px' }}>
          {provisioningStalled ? (
            // Recovery panel — shown after STALL_AFTER_ATTEMPTS without
            // an `invite_token`. Replaces the historical silent fall-
            // through to `/register?email=...` (which had no token and
            // dead-ended at /login). The polling continues in the
            // background; if the activation token finally arrives,
            // `setProvisioningStalled(false)` flips us into the normal
            // "Criar Minha Conta" CTA branch automatically.
            <Box>
              <AccentNote title="Sua conta está demorando mais que o esperado" sx={{ mb: '16px' }}>
                Seu pagamento foi confirmado e estamos preparando o seu acesso. Já enviamos o link de ativação por e-mail. Se preferir, podemos reenviá-lo agora.
              </AccentNote>

              {(maskedEmail || email) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '14px', color: MUTED58 }}>
                  <EmailIcon sx={{ fontSize: 16 }} />
                  <Typography sx={{ fontSize: 12.5 }}>
                    Link de ativação enviado para{' '}
                    <Box component="strong" sx={{ fontFamily: HEADING, fontWeight: 600, color: 'var(--color-text)' }}>
                      {maskedEmail || email}
                    </Box>
                  </Typography>
                </Box>
              )}

              {activationResendInfo && <AccentNote sx={{ mb: '14px' }}>{activationResendInfo}</AccentNote>}
              {activationResendError && <InkNote sx={{ mb: '14px' }}>{activationResendError}</InkNote>}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Box
                  component="button"
                  type="button"
                  onClick={handleResendActivationEmail}
                  disabled={resendingActivation || !email}
                  sx={primaryBtnSx}
                >
                  <EmailIcon sx={{ fontSize: 16 }} />
                  {resendingActivation ? 'Reenviando link...' : 'Reenviar link de ativação'}
                </Box>
                <Box component="button" type="button" onClick={handleRecheckNow} sx={secondaryBtnSx}>
                  {pollExhausted ? 'Tentar de novo' : 'Verificar agora'}
                </Box>
                <Box component="button" type="button" onClick={openSupportForm} sx={{ ...linkBtnSx, color: MUTED58, '&:hover': { color: 'var(--color-text)' } }}>
                  Falar com o suporte ({SUPPORT_EMAIL})
                </Box>
              </Box>

              {supportFormOpen && (
                <Box
                  component="form"
                  onSubmit={handleSubmitSupportRequest}
                  sx={{
                    mt: '16px',
                    p: '16px',
                    border: '1px solid var(--color-divider)',
                    background: 'transparent',
                    display: 'grid',
                    gap: '12px',
                  }}
                >
                  <Box>
                    <Typography sx={{ fontFamily: HEADING, fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>
                      Abrir chamado de ativação
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: MUTED72, mt: '3px', lineHeight: 1.5 }}>
                      Informe seus dados para a equipe localizar o pagamento e liberar o acesso.
                    </Typography>
                  </Box>
                  {supportError && <InkNote>{supportError}</InkNote>}
                  {supportSuccess && <AccentNote>{supportSuccess}</AccentNote>}
                  <Box>
                    <Box component="label" htmlFor="support-name" sx={labelSx}>
                      Nome
                    </Box>
                    <input
                      id="support-name"
                      className="ind-input"
                      value={supportForm.name}
                      onChange={(event) => handleSupportFieldChange('name', event.target.value)}
                      required
                      disabled={supportSubmitting}
                      style={inputStyle}
                    />
                  </Box>
                  <Box>
                    <Box component="label" htmlFor="support-email" sx={labelSx}>
                      E-mail
                    </Box>
                    <input
                      id="support-email"
                      className="ind-input"
                      type="email"
                      value={supportForm.email}
                      onChange={(event) => handleSupportFieldChange('email', event.target.value)}
                      required
                      disabled={supportSubmitting}
                      style={inputStyle}
                    />
                  </Box>
                  <Box>
                    <Box component="label" htmlFor="support-company" sx={labelSx}>
                      Empresa
                    </Box>
                    <input
                      id="support-company"
                      className="ind-input"
                      value={supportForm.company_name}
                      onChange={(event) => handleSupportFieldChange('company_name', event.target.value)}
                      disabled={supportSubmitting}
                      style={inputStyle}
                    />
                  </Box>
                  <Box>
                    <Box component="label" htmlFor="support-description" sx={labelSx}>
                      O que aconteceu?
                    </Box>
                    <textarea
                      id="support-description"
                      className="ind-input"
                      value={supportForm.description}
                      onChange={(event) => handleSupportFieldChange('description', event.target.value)}
                      required
                      rows={3}
                      disabled={supportSubmitting}
                      style={{ ...inputStyle, minHeight: 76, padding: '9px 11px', resize: 'vertical', lineHeight: 1.5 }}
                    />
                    <Box sx={{ fontFamily: HEADING, fontSize: '9px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'color-mix(in srgb, var(--color-text) 52%, transparent)', mt: '4px' }}>
                      Não inclua senha, código de verificação ou dados do cartão.
                    </Box>
                  </Box>
                  <Box component="button" type="submit" disabled={supportSubmitting} sx={{ ...primaryBtnSx, minHeight: 40 }}>
                    {supportSubmitting ? 'Enviando...' : 'Enviar chamado'}
                  </Box>
                </Box>
              )}

              {provisioning && !pollExhausted && (
                <Typography sx={{ display: 'block', mt: '16px', fontSize: 11.5, color: MUTED55, textAlign: 'center', lineHeight: 1.5 }}>
                  Continuamos verificando em segundo plano. Assim que sua conta estiver pronta, você será levado automaticamente para a ativação.
                </Typography>
              )}

              {/* "What's next" preview — keeps the wait feeling like
                  progress, not a stall. */}
              <Box sx={{ mt: '20px', pt: '16px', borderTop: '1px dashed var(--color-neutral-300)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '12px', flexWrap: 'wrap' }}>
                  <Box component="span" sx={{ fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: MUTED58 }}>
                    O que vem a seguir
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  <Box component="span" sx={{ fontFamily: HEADING, fontSize: '9px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED55 }}>
                    Após ativar o acesso
                  </Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' }, gap: '10px' }}>
                  {[
                    { n: '01', title: 'Cadastre suas aeronaves', body: 'Suba o RAB e a Zulbot extrai matrícula, modelo e tripulação.' },
                    { n: '02', title: 'Conecte o WhatsApp', body: 'Pilotos enviam fuel, logbook e voz — a I.A. organiza tudo.' },
                    { n: '03', title: 'Rode o primeiro eFRAT', body: 'Análise de risco em ~30s contra 90 anos de NTSB.' },
                  ].map((step) => (
                    <Box key={step.n} sx={{ p: '12px', border: '1px solid var(--color-divider)', background: 'transparent', minWidth: 0 }}>
                      <Box sx={{ fontFamily: HEADING, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: ACC7, mb: '4px' }}>
                        {step.n}
                      </Box>
                      <Box sx={{ fontSize: 12.5, fontWeight: 600, letterSpacing: '-0.01em', mb: '3px', lineHeight: 1.3 }}>
                        {step.title}
                      </Box>
                      <Box sx={{ fontSize: 11.5, color: MUTED72, lineHeight: 1.45 }}>{step.body}</Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          ) : provisioning ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', my: '8px' }}>
              <CircularProgress size={28} sx={{ color: ACC }} />
              <Typography sx={{ fontWeight: 700, fontSize: 14.5 }}>
                Estamos preparando sua conta...
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: MUTED72, textAlign: 'center', lineHeight: 1.5 }}>
                Isso pode levar alguns minutos enquanto criamos seu ambiente. Por favor, aguarde nesta tela.
              </Typography>
            </Box>
          ) : verificationRequired ? (
            <Box>
              <AccentNote title="Verifique seu email para continuar" sx={{ mb: '16px' }}>
                Seu pagamento foi confirmado. Antes de ativar o acesso, valide o código enviado para o email da compra.
              </AccentNote>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '14px', color: MUTED58 }}>
                <EmailIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: 12.5 }}>
                  Codigo enviado para{' '}
                  <Box component="strong" sx={{ fontFamily: HEADING, fontWeight: 600, color: 'var(--color-text)' }}>
                    {maskedEmail || email || 'seu email'}
                  </Box>
                </Typography>
              </Box>

              <Box sx={{ mb: '14px' }}>
                <Box component="label" htmlFor="billing-verification-code" sx={{ ...labelSx, color: ACC7 }}>
                  Codigo de verificacao
                </Box>
                <input
                  id="billing-verification-code"
                  className="ind-input"
                  value={verificationCode}
                  onChange={(event) => {
                    setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                    setError('');
                  }}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  style={{
                    ...inputStyle,
                    minHeight: 44,
                    fontFamily: 'var(--font-heading)',
                    fontSize: 20,
                    fontWeight: 600,
                    letterSpacing: '0.3em',
                    textAlign: 'center',
                    border: `1px solid ${ACC}`,
                  }}
                />
              </Box>

              {verificationMessage && <AccentNote sx={{ mb: '14px' }}>{verificationMessage}</AccentNote>}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Box
                  component="button"
                  type="button"
                  onClick={handleVerifyCode}
                  disabled={verifyingCode || verificationCode.length !== 6}
                  sx={primaryBtnSx}
                >
                  {verifyingCode ? 'Validando codigo...' : 'Validar codigo'}
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendingCode}
                  sx={{ ...linkBtnSx, color: MUTED58, '&:hover': { color: 'var(--color-text)' } }}
                >
                  {resendingCode ? 'Reenviando codigo...' : 'Reenviar codigo'}
                </Box>
              </Box>
            </Box>
          ) : needsAccount ? (
            <Box>
              <AccentNote title="Crie sua conta para acessar a plataforma" sx={{ mb: '16px' }}>
                Seu pagamento foi confirmado. Agora, crie sua conta de acesso em menos de 1 minuto.
              </AccentNote>

              {email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '14px', color: MUTED58 }}>
                  <EmailIcon sx={{ fontSize: 16 }} />
                  <Typography sx={{ fontSize: 12.5 }}>
                    Email:{' '}
                    <Box component="strong" sx={{ fontFamily: HEADING, fontWeight: 600, color: 'var(--color-text)' }}>
                      {email}
                    </Box>
                  </Typography>
                </Box>
              )}

              <AccentNote title="Também enviamos o link por e-mail" sx={{ mb: '14px' }}>
                Se você fechar esta janela, abra o link de ativação que enviamos para{' '}
                <Box component="strong" sx={{ fontFamily: HEADING, fontWeight: 600 }}>
                  {maskedEmail || email || 'seu e-mail da compra'}
                </Box>{' '}
                para concluir o acesso.
              </AccentNote>

              <Typography sx={{ fontSize: 11.5, color: MUTED55, display: 'block', mb: '14px', lineHeight: 1.5 }}>
                Seu email já foi validado. Continue para ativar o acesso do administrador principal da empresa.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Box component="button" type="button" onClick={handleCreateAccount} sx={primaryBtnSx}>
                  <PersonAddIcon sx={{ fontSize: 17 }} />
                  Criar Minha Conta
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={handleGoToLogin}
                  sx={{ ...linkBtnSx, color: MUTED58, '&:hover': { color: 'var(--color-text)' } }}
                >
                  <LoginIcon sx={{ fontSize: 15 }} />
                  Já tenho conta — Fazer login
                </Box>
              </Box>
            </Box>
          ) : (
            <Box>
              {/* Receipt — hairline rack with dashed row dividers */}
              <Box sx={{ border: '1px solid var(--color-divider)', mb: '20px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    p: '9px 13px',
                    borderBottom: '1px dashed var(--color-neutral-300)',
                  }}
                >
                  <Box component="span" sx={{ fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED58 }}>
                    RECIBO · COBRANÇA RECORRENTE
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  <Box component="span" sx={{ fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    ZLB · PRO
                  </Box>
                </Box>

                {receiptLines.map(([key, value]) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '10px',
                      p: '8px 13px',
                      borderBottom: '1px dashed var(--color-neutral-200)',
                      '&:last-of-type': { borderBottom: 'none' },
                    }}
                  >
                    <Box component="span" sx={{ fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTED58 }}>
                      {key}
                    </Box>
                    <Box component="span" sx={{ fontFamily: HEADING, fontSize: 13, fontWeight: 600, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>
                      {value}
                    </Box>
                  </Box>
                ))}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    p: '9px 13px',
                    borderTop: '1px dashed var(--color-neutral-300)',
                    flexWrap: 'wrap',
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => {
                      /* Stripe sends the receipt PDF via email; point there
                         until a backend recibo endpoint is exposed. */
                      window.open('https://dashboard.stripe.com/', '_blank', 'noopener');
                    }}
                    sx={linkBtnSx}
                  >
                    <DownloadIcon sx={{ fontSize: 15 }} />
                    Baixar recibo (PDF)
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  <Typography sx={{ fontSize: 11.5, color: MUTED55 }}>
                    Gerenciar assinatura após ativar seu acesso.
                  </Typography>
                </Box>
              </Box>

              {/* Próximos passos — square glyph-plate rows */}
              <Box component="span" sx={{ display: 'block', fontFamily: HEADING, fontSize: '10px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: MUTED58, mb: '10px' }}>
                PRÓXIMOS PASSOS · PRÉ-VOO
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mb: '20px' }}>
                {NEXT_STEPS.map((step) => (
                  <Box
                    key={step.code}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      p: '10px 12px',
                      border: '1px solid var(--color-divider)',
                      borderLeft: `3px solid ${ACC}`,
                      background: 'transparent',
                    }}
                  >
                    <Box
                      sx={{
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        width: 26,
                        height: 24,
                        border: '1px solid var(--color-divider)',
                        fontFamily: HEADING,
                        fontSize: 11,
                        fontWeight: 600,
                        color: MUTED58,
                      }}
                    >
                      {step.code}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
                        {step.title}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: MUTED72, mt: '2px' }}>
                        {step.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box component="button" type="button" onClick={handleGoToLogin} sx={primaryBtnSx}>
                Acessar Plataforma
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </Box>

              <Typography sx={{ fontSize: 12, color: MUTED58, textAlign: 'center', mt: '12px' }}>
                Recebemos um e-mail de confirmação em{' '}
                <Box component="span" sx={{ fontFamily: HEADING, fontWeight: 600, color: 'var(--color-text)' }}>
                  {maskedEmail || email || 'seu email da compra'}
                </Box>
              </Typography>
            </Box>
          )}

          {error && <InkNote sx={{ mt: '16px' }}>{error}</InkNote>}
        </Box>
      </Box>
    </Box>
  );
}
